import React, { useCallback, useMemo, useState } from 'react';
import { api } from '@/api/client';
import { ApiError } from '@/api/http';
import { workspacePickersAsUsers } from '@/api/services/workspaceUsers';
import ConfirmRecipientDeleteModal from '@/components/ConfirmRecipientDeleteModal';
import { AdminPageSections, AutoRefreshToolbar, BulkActionBar, BulkActionButton, CollapsiblePanel, StickyFormBar, useCollapsiblePanelState } from '@/components/common';
import EditRecipientModal from '@/components/EditRecipientModal';
import KnownChatsModal, { type KnownChat } from '@/components/KnownChatsModal';
import RecipientEnabledField from '@/components/RecipientEnabledField';
import PageHeader from '@/components/PageHeader';
import PageHeaderOverflowMenu from '@/components/PageHeaderOverflowMenu';
import FloatingActionButton from '@/components/navigation/FloatingActionButton';
import { useNavigation } from '@/context/NavigationContext';
import SendMessageModal from '@/components/SendMessageModal';
import {
  DataTable,
  TruncatedCell,
  TableActionBar,
  TABLE_ACTION_MIN_WIDTH,
  tableActionsColumnClass,
  type DataTableColumn,
} from '@/components/table';
import { exportTableCsv } from '@/utils/exportTableCsv';
import {
  EmptyState,
  ErrorBanner,
  LoadingButton,
  RecipientCell,
  DeliveryRouteCell,
  RecipientReadinessBadge,
  StatusBadge,
} from '@/components/ui';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import type { Recipient, User } from '@/types';
import {
  formatKnownChatsEmptyState,
} from '@/utils/knownChatsMessages';
import {
  formatTelegramDiagnostics,
  formatTelegramDiagnosticsDetailed,
  formatTelegramError,
  isNumericChatId,
  isPlainUsername,
  isTelethonSessionBusyError,
  TELETHON_SESSION_BUSY_CACHED_MESSAGE,
  extractBusyKnownChats,
  meRecipientWarning,
  type TelegramSenderMode,
} from '@/utils/telegramErrors';
import { formatApiError } from '@/utils/formatApiError';
import {
  canCreateRecipient as userCanCreateRecipient,
  canDeleteRecipient as userCanDeleteRecipient,
  canEditRecipient as userCanEditRecipient,
  canManageRecipients as userCanManageRecipients,
  canTestRecipient as userCanTestRecipient,
} from '@/utils/navCapabilities';
import { recipientRowSearchValue } from '@/utils/recipientDisplay';
import { visibilityBadgeLabel } from '@/utils/recipientVisibility';
import { isPlatformOwnerRole } from '@/utils/teamRoles';
import {
  type CreateResolutionMeta,
  type TelegramDeliveryProvider,
  buildRecipientCreatePayload,
  DELIVERY_PROVIDER_OPTIONS,
  knownChatCreatePayload,
  resolvePreviewToCreateFields,
} from '@/utils/knownChatRecipient';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';

const PANEL_SCOPE = 'recipients-page';

function normalizeChatId(value: string): string {
  return value.trim().replace(/^@+/, '');
}

function recipientOwnerLabel(r: Recipient): string {
  if (r.owner_name?.trim()) return r.owner_name.trim();
  if (r.owner_email?.trim()) return r.owner_email.trim();
  if (r.owner_user_id) return r.owner_user_id.slice(0, 8);
  return 'Shared';
}

export default function RecipientsPage() {
  const { toast } = useToast();
  const { me } = useMe();
  const canAssignOwner = userCanManageRecipients(me);
  const canCreate = userCanCreateRecipient(me);
  const { isCompactNav } = useNavigation();
  const canEdit = userCanEditRecipient(me);
  const canDelete = userCanDeleteRecipient(me);
  const canSendTest = userCanTestRecipient(me);
  const canBrowseKnownChats = isPlatformOwnerRole(me?.role);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [teamUsers, setTeamUsers] = useState<User[]>([]);
  const [ownerUserId, setOwnerUserId] = useState('');
  const [error, setError] = useState('');
  const [chatId, setChatId] = useState('');
  const [name, setName] = useState('');
  const [deleteRecipient, setDeleteRecipient] = useState<Recipient | null>(null);
  const [editRecipient, setEditRecipient] = useState<Recipient | null>(null);
  const [sendRecipient, setSendRecipient] = useState<Recipient | null>(null);
  const [showSendForm, setShowSendForm] = useState(false);
  const [tgStatus, setTgStatus] = useState<{
    session_exists: boolean;
    bot_token_configured: boolean;
    api_id_configured: boolean;
    sender_mode: TelegramSenderMode;
  } | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [refreshingKnownChats, setRefreshingKnownChats] = useState(false);
  const [knownChats, setKnownChats] = useState<KnownChat[] | null>(null);
  const [knownChatsNotice, setKnownChatsNotice] = useState<string | null>(null);
  const [knownChatsRefreshError, setKnownChatsRefreshError] = useState<string | null>(null);
  const [knownChatsLastRefreshed, setKnownChatsLastRefreshed] = useState<string | null>(null);
  const [creatingKnownChatId, setCreatingKnownChatId] = useState<number | null>(null);
  const [resolvingCreate, setResolvingCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createEnabled, setCreateEnabled] = useState(true);
  const [createVisibility, setCreateVisibility] = useState<'private' | 'workspace'>('private');
  const [createResolutionMeta, setCreateResolutionMeta] = useState<CreateResolutionMeta | null>(
    null,
  );
  const [createDeliveryProvider, setCreateDeliveryProvider] =
    useState<TelegramDeliveryProvider>('auto');
  const [lastTestById, setLastTestById] = useState<Record<string, { ok: boolean; message: string }>>({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [addPanelExpanded, setAddPanelExpanded] = useCollapsiblePanelState(
    PANEL_SCOPE,
    'add-recipient',
    true,
  );
  const [listPanelExpanded, setListPanelExpanded] = useCollapsiblePanelState(
    PANEL_SCOPE,
    'recipient-list',
    true,
  );
  const bulk = useBulkSelection<Recipient>();

  const fetchRecipients = useCallback(async () => {
    setRecipients(await api.recipients.list());
    if (canAssignOwner) {
      try {
        setTeamUsers(workspacePickersAsUsers((await api.workspaceUsers.pickers()).items));
      } catch {
        setTeamUsers([]);
      }
    }
    try {
      const st = await api.telegram.status();
      setTgStatus({
        session_exists: st.session_exists,
        bot_token_configured: st.bot_token_configured,
        api_id_configured: st.api_id_configured,
        sender_mode: (st.sender_mode as TelegramSenderMode) || 'auto',
      });
    } catch {
      setTgStatus(null);
    }
    setError('');
  }, [canAssignOwner]);

  const { autoRefresh, initialLoading } = useAutoRefreshPolling({
    hasData: () => recipients.length > 0,
    load: async () => {
      try {
        await fetchRecipients();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load recipients');
        throw e;
      }
    },
    autoRefresh: {
      intervalMs: 30000,
      deps: [canAssignOwner],
      storageKey: 'recipients-auto-refresh',
    },
  });

  const reload = useCallback(async () => {
    await autoRefresh.refresh();
  }, [autoRefresh]);

  const submitCreateRecipient = useCallback(
    async (payload: ReturnType<typeof buildRecipientCreatePayload>) => {
      setCreating(true);
      setError('');
      try {
        const created = await api.recipients.create(payload);
        setChatId('');
        setName('');
        setOwnerUserId('');
        setCreateEnabled(true);
        setCreateVisibility('private');
        setCreateResolutionMeta(null);
        setCreateDeliveryProvider('bot');
        setListPanelExpanded(true);
        const successMessage = created.was_updated
          ? `Recipient updated (${created.display_name || created.chat_id})`
          : `Recipient added (${created.display_name || created.chat_id})`;
        toast(successMessage, 'success');
        if (created.resolution_warning) {
          toast(created.resolution_warning, 'info');
        }
        setRecipients((prev) => {
          const without = prev.filter((r) => r.id !== created.id);
          return [created, ...without];
        });
        await reload();
        return created;
      } catch (err) {
        const message = formatApiError(err, 'Create failed');
        setError(message);
        toast(message, 'error');
        return null;
      } finally {
        setCreating(false);
      }
    },
    [reload, toast, setListPanelExpanded],
  );

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const normalized = normalizeChatId(chatId);
    if (!normalized) {
      const message = 'Telegram chat ID is required';
      setError(message);
      toast(message, 'error');
      return;
    }
    await submitCreateRecipient(
      buildRecipientCreatePayload({
        chat_id: chatId,
        display_name: name,
        is_enabled: createEnabled,
        visibility: createVisibility,
        owner_user_id: canAssignOwner && ownerUserId ? ownerUserId : undefined,
        telegram_username: createResolutionMeta?.telegram_username,
        telegram_resolution_method: createResolutionMeta?.telegram_resolution_method,
        telegram_resolved_chat_id: createResolutionMeta?.telegram_resolved_chat_id,
        delivery_provider: createResolutionMeta?.delivery_provider ?? createDeliveryProvider,
        source: createResolutionMeta?.source ?? 'manual',
      }),
    );
  }

  const handleKnownChatUse = useCallback(
    async (chat: KnownChat) => {
      setCreatingKnownChatId(chat.chat_id);
      try {
        const created = await submitCreateRecipient(
          knownChatCreatePayload(chat, {
            is_enabled: createEnabled,
            visibility: createVisibility,
            owner_user_id: canAssignOwner && ownerUserId ? ownerUserId : undefined,
          }),
        );
        if (created) {
          setKnownChats(null);
        }
      } finally {
        setCreatingKnownChatId(null);
      }
    },
    [canAssignOwner, createEnabled, createVisibility, ownerUserId, submitCreateRecipient],
  );

  const toggle = useCallback(
    async (r: Recipient) => {
      try {
        await api.recipients.update(r.id, { is_enabled: !r.is_enabled });
        toast(r.is_enabled ? 'Recipient disabled' : 'Recipient enabled', 'info');
        await reload();
      } catch (err) {
        const message = formatApiError(err, 'Update failed');
        setError(message);
        toast(message, 'error');
      }
    },
    [toast, reload]
  );

  async function openKnownChats() {
    if (checkingUpdates) return;
    setCheckingUpdates(true);
    setKnownChatsNotice(null);
    setKnownChatsRefreshError(null);
    try {
      const cached = await api.telegram.updates();
      setKnownChats(cached.updates ?? []);
      setKnownChatsLastRefreshed(cached.last_refreshed_at ?? null);
      if (cached.refreshing && !cached.updates?.length) {
        setKnownChatsNotice('Loading chats from the Telethon session…');
      }
    } catch (err) {
      if (isTelethonSessionBusyError(err) && err instanceof ApiError) {
        const busyCached = extractBusyKnownChats<KnownChat>(err.detail);
        if (busyCached?.length) {
          setKnownChats(busyCached);
          setKnownChatsNotice(TELETHON_SESSION_BUSY_CACHED_MESSAGE);
          return;
        }
        setKnownChats([]);
        setKnownChatsNotice('Telegram session is busy. Try again shortly.');
        return;
      }
      toast(err instanceof Error ? err.message : 'Failed to load known chats', 'error');
    } finally {
      setCheckingUpdates(false);
    }
  }

  async function refreshKnownChats(options?: { background?: boolean }) {
    if (refreshingKnownChats) return;
    const background = options?.background ?? false;
    if (!background) {
      setRefreshingKnownChats(true);
      setKnownChatsRefreshError(null);
    }
    let openedChats: KnownChat[] = knownChats ?? [];
    try {
      const result = await api.telegram.refreshKnownChats();
      setKnownChatsLastRefreshed(result.last_refreshed_at ?? null);
      if (result.updates?.length) {
        setKnownChats(result.updates);
        setKnownChatsNotice(null);
        setKnownChatsRefreshError(null);
        return;
      }
      if (!openedChats.length) {
        setKnownChats([]);
        setKnownChatsRefreshError(formatKnownChatsEmptyState(result));
      }
    } catch (err) {
      if (isTelethonSessionBusyError(err) && err instanceof ApiError) {
        const busyCached = extractBusyKnownChats<KnownChat>(err.detail) ?? openedChats;
        if (busyCached.length) {
          setKnownChats(busyCached);
          setKnownChatsNotice(TELETHON_SESSION_BUSY_CACHED_MESSAGE);
          return;
        }
        if (!background) {
          setKnownChats([]);
          setKnownChatsNotice('Telegram session is busy. Try again shortly.');
        }
        return;
      }
      const message = err instanceof Error ? err.message : 'Refresh failed';
      if (!background) {
        setKnownChatsRefreshError(message);
        toast(message, 'error');
      }
    } finally {
      if (!background) {
        setRefreshingKnownChats(false);
      }
    }
  }

  async function resolveChatIdInput(value: string, onResolved?: (id: string) => void) {
    const trimmed = value.trim();
    if (!trimmed || isNumericChatId(trimmed)) return;
    setResolvingCreate(true);
    try {
      const result = await api.recipients.resolveChatId(trimmed);
      if (result.resolved_chat_id && !result.error_code) {
        const { chat_id: resolvedChatId, meta } = resolvePreviewToCreateFields(trimmed, result);
        onResolved?.(resolvedChatId);
        setCreateResolutionMeta(meta);
        if (meta?.telegram_resolution_method?.startsWith('telethon_')) {
          toast(`Resolved ${trimmed} → ${resolvedChatId} (Telethon)`, 'success');
        } else {
          toast(`Resolved ${trimmed} → ${resolvedChatId}`, 'success');
        }
      } else {
        toast(
          formatTelegramError(result.error_message, result.error_code, {
            telethonResolutionAttempted:
              (result as { telethonResolutionAttempted?: boolean }).telethonResolutionAttempted,
            failureStep: (result as { failureStep?: string }).failureStep,
          }),
          'error',
        );
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Resolve failed', 'error');
    } finally {
      setResolvingCreate(false);
    }
  }

  const resolveTelegram = useCallback(
    async (r: Recipient) => {
      setResolvingId(r.id);
      try {
        const result = await api.recipients.resolveTelegram(r.id);
        if (result.resolved_chat_id && !result.error_code) {
          toast(
            result.updated
              ? `Resolved ${r.chat_id} → ${result.resolved_chat_id}`
              : `Chat ID ${result.resolved_chat_id}`,
            'success'
          );
          await reload();
        } else if (result.error_code === 'telegram_bot_chat_not_found') {
          toast(
            `No updates found. Ask @${r.chat_id.replace(/^@/, '')} to open the bot and send /start.`,
            'error'
          );
        } else {
          toast(formatTelegramError(result.error_message, result.error_code), 'error');
        }
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Resolve failed', 'error');
      } finally {
        setResolvingId(null);
      }
    },
    [toast, reload]
  );

  const testTelegram = useCallback(
    async (r: Recipient) => {
      setTestingId(r.id);
      try {
        const result = await api.recipients.testTelegram(r.id);
        if (result.success) {
          const methodLabel =
            result.finalSendMode === 'telethon' || result.finalMode === 'telethon'
              ? 'Telethon'
              : result.finalSendMode === 'bot' || result.finalMode === 'bot'
                ? 'Bot'
                : result.sender_mode || 'unknown';
          const diagnostic = formatTelegramDiagnostics(result);
          const parts = [`Sent via ${methodLabel}`];
          if (diagnostic) {
            parts.push(diagnostic);
          } else if (result.resolvedChatId) {
            parts.push(`resolved ID ${result.resolvedChatId}`);
          } else {
            parts.push(`to ${result.chat_id}`);
          }
          const message = parts.join(' · ');
          setLastTestById((prev) => ({ ...prev, [r.id]: { ok: true, message } }));
          toast(message, 'success');
        } else {
          const message = formatTelegramError(result.error_message, result.error_code, {
            finalSendMode: result.finalSendMode ?? result.finalMode,
            failureStep: result.failureStep,
            telethonResolutionAttempted: result.telethonResolutionAttempted,
          });
          const diagnostic = formatTelegramDiagnostics(result);
          const detailLines = formatTelegramDiagnosticsDetailed({
            ...result,
            dialogCacheUsed: result.entitySource?.includes('dialog') ?? undefined,
            contactsUsed: result.entitySource?.includes('contact') ?? undefined,
            accessHashCached: result.entitySource === 'cached' ? true : undefined,
            result: 'failed',
          });
          const detail = detailLines.length
            ? `${message}\n${detailLines.join('\n')}`
            : diagnostic
              ? `${message} (${diagnostic})`
              : message;
          setLastTestById((prev) => ({ ...prev, [r.id]: { ok: false, message: detail } }));
          toast(detail, 'error');
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Test send failed';
        setLastTestById((prev) => ({ ...prev, [r.id]: { ok: false, message } }));
        toast(message, 'error');
      } finally {
        setTestingId(null);
      }
    },
    [toast]
  );

  const botConfigured = tgStatus?.bot_token_configured ?? false;
  const telethonConfigured = tgStatus?.api_id_configured ?? false;
  const telethonSessionReady = telethonConfigured && (tgStatus?.session_exists ?? false);
  const senderMode: TelegramSenderMode = tgStatus?.sender_mode ?? 'auto';
  const telegramReady = botConfigured || (tgStatus?.session_exists ?? false);

  const canResolve = useCallback(
    (r: Recipient): boolean =>
      r.channel === 'telegram' &&
      telegramReady &&
      isPlainUsername(r.chat_id) &&
      !r.telegram_resolution_method?.startsWith('telethon_'),
    [telegramReady]
  );

  const canTest = useCallback(
    (r: Recipient): boolean => r.channel === 'telegram' && r.is_enabled && telegramReady,
    [telegramReady]
  );

  const recipientColumns = useMemo<DataTableColumn<Recipient>[]>(
    () => [
      {
        id: 'recipient',
        header: 'Recipient',
        width: 180,
        minWidth: 140,
        maxWidth: 280,
        sortValue: (r) => recipientRowSearchValue(r),
        csvValue: (r) => recipientRowSearchValue(r),
        render: (r) => <RecipientCell recipient={r} />,
      },
      {
        id: 'owner',
        header: 'Created by',
        width: 130,
        minWidth: 100,
        maxWidth: 180,
        sortValue: (r) => recipientOwnerLabel(r),
        csvValue: (r) => recipientOwnerLabel(r),
        render: (r) => <TruncatedCell value={recipientOwnerLabel(r)} />,
      },
      {
        id: 'route',
        header: 'Delivery route',
        width: 160,
        minWidth: 120,
        maxWidth: 220,
        sortValue: (r) => r.telegram_resolved_chat_id || r.chat_id,
        csvValue: (r) => r.telegram_resolved_chat_id || r.chat_id,
        render: (r) => <DeliveryRouteCell recipient={r} />,
      },
      {
        id: 'readiness',
        header: 'Readiness',
        width: 150,
        minWidth: 120,
        maxWidth: 200,
        resizable: false,
        render: (r) => (
          <div className="recipient-readiness-cell">
            <RecipientReadinessBadge
              recipient={r}
              botConfigured={botConfigured}
              telethonConfigured={telethonConfigured}
              telethonSessionReady={telethonSessionReady}
              senderMode={senderMode}
            />
            {lastTestById[r.id] ? (
              <div className={`muted recipient-last-test recipient-last-test--${lastTestById[r.id].ok ? 'ok' : 'fail'}`}>
                Last test: {lastTestById[r.id].message}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'visibility',
        header: 'Visibility',
        width: 120,
        minWidth: 100,
        maxWidth: 140,
        sortValue: (r) => r.visibility || 'private',
        csvValue: (r) => r.visibility || 'private',
        render: (r) => (
          <StatusBadge variant={r.visibility === 'workspace' || r.visibility === 'organization' ? 'info' : 'neutral'}>
            {visibilityBadgeLabel(r.visibility)}
          </StatusBadge>
        ),
      },
      {
        id: 'subs',
        header: 'Subs',
        width: 70,
        minWidth: 56,
        maxWidth: 90,
        resizable: false,
        sortValue: (r) => r.subscription_count,
        csvValue: (r) => r.subscription_count,
        render: (r) => <TruncatedCell value={r.subscription_count} tooltip={false} />,
      },
      {
        id: 'enabled',
        header: 'Enabled',
        width: 90,
        minWidth: 72,
        maxWidth: 100,
        resizable: false,
        sortValue: (r) => (r.is_enabled ? 1 : 0),
        csvValue: (r) => (r.is_enabled ? 'Yes' : 'No'),
        render: (r) => (
          <StatusBadge variant={r.is_enabled ? 'success' : 'neutral'}>
            {r.is_enabled ? 'Yes' : 'No'}
          </StatusBadge>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        width: TABLE_ACTION_MIN_WIDTH.recipients,
        minWidth: TABLE_ACTION_MIN_WIDTH.recipients,
        resizable: false,
        actions: true,
        className: tableActionsColumnClass('recipients'),
        render: (r) => (
          <TableActionBar>
            {canEdit && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditRecipient(r)}>
                Edit
              </button>
            )}
            {canEdit && (
              <button type="button" className="btn btn-sm" onClick={() => toggle(r)}>
                {r.is_enabled ? 'Disable' : 'Enable'}
              </button>
            )}
            {canSendTest && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={!canTest(r) || testingId === r.id}
                title={
                  !canTest(r)
                    ? 'Enable recipient and configure Telegram to send a test'
                    : 'Send a test message'
                }
                onClick={() => testTelegram(r)}
              >
                {testingId === r.id ? 'Sending…' : 'Send test'}
              </button>
            )}
            {canSendTest && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSendRecipient(r)}>
                Message
              </button>
            )}
            {canEdit && canResolve(r) && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={resolvingId === r.id}
                title="Resolve username via bot or Telethon"
                onClick={() => resolveTelegram(r)}
              >
                {resolvingId === r.id ? 'Resolving…' : 'Resolve ID'}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => setDeleteRecipient(r)}
                aria-label={`Remove recipient ${r.display_name || r.chat_id}`}
              >
                Remove
              </button>
            )}
          </TableActionBar>
        ),
      },
    ],
    [botConfigured, telethonConfigured, telethonSessionReady, senderMode, canEdit, canDelete, canSendTest, testingId, resolvingId, toggle, canTest, canResolve, testTelegram, resolveTelegram, lastTestById]
  );

  return (
    <div
      className={`admin-page admin-page--recipients${
        canCreate && isCompactNav ? ' admin-page--with-fab' : ''
      }`}
    >
      <PageHeader
        title="Recipients"
        subtitle="Delivery targets — readiness shows how each recipient will receive alerts"
      >
        <AutoRefreshToolbar control={autoRefresh} intervalLabel="Every 30s" />
        {canCreate && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setAddPanelExpanded(true)}
            aria-label="Add a new recipient"
          >
            Add recipient
          </button>
        )}
        {canCreate && canBrowseKnownChats && telegramReady && !isCompactNav ? (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={checkingUpdates}
            onClick={openKnownChats}
            aria-label="Known chats"
          >
            {checkingUpdates ? 'Checking…' : 'Known chats'}
          </button>
        ) : null}
        {canSendTest && !isCompactNav ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setShowSendForm(true)}
            aria-label="Send a custom message"
          >
            Send message…
          </button>
        ) : null}
        {isCompactNav && (canBrowseKnownChats || canSendTest) ? (
          <PageHeaderOverflowMenu
            items={[
              {
                id: 'known-chats',
                label: checkingUpdates ? 'Checking…' : 'Known chats',
                hidden: !(canCreate && canBrowseKnownChats && telegramReady),
                disabled: checkingUpdates,
                onClick: openKnownChats,
              },
              {
                id: 'send-message',
                label: 'Send message…',
                hidden: !canSendTest,
                onClick: () => setShowSendForm(true),
              },
            ]}
          />
        ) : null}
      </PageHeader>

      {!canCreate && !canEdit && (
        <div className="alert alert-info" role="status">
          You can view recipients but do not have permission to add or edit them.
        </div>
      )}

      {tgStatus && !tgStatus.bot_token_configured && !tgStatus.session_exists && (
        <div className="alert alert-error" role="alert">
          Telegram is not configured. Add a bot token or Telethon session in Configuration.
        </div>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <AdminPageSections>
        {canCreate && (
          <div id="recipient-create-panel">
          <CollapsiblePanel
            id="add-recipient"
            storageScope={PANEL_SCOPE}
            title="Add recipient"
            subtitle="Telegram chat ID or username"
            expanded={addPanelExpanded}
            onExpandedChange={setAddPanelExpanded}
            defaultExpanded
          >
            <form className="recipient-create-form" onSubmit={create}>
              <div className="admin-form-grid">
                <label>
                  Channel
                  <select value="telegram" disabled title="Only Telegram is supported today" aria-label="Channel">
                    <option value="telegram">Telegram</option>
                  </select>
                </label>
                <label className="admin-form-grid__span-2">
                  Telegram chat ID
                  <div className="form-inline">
                    <input
                      value={chatId}
                      onChange={(e) => {
                        setChatId(e.target.value);
                        setCreateResolutionMeta(null);
                      }}
                      placeholder="numeric id, @username, or me"
                      required
                      aria-label="Telegram chat ID"
                    />
                    {isPlainUsername(chatId) && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={resolvingCreate || !telegramReady}
                        title={telegramReady ? 'Resolve username to numeric chat ID' : 'Configure Telegram first'}
                        onClick={() => resolveChatIdInput(chatId, setChatId)}
                      >
                        {resolvingCreate ? 'Resolving…' : 'Resolve ID'}
                      </button>
                    )}
                  </div>
                  {chatId.trim().toLowerCase() === 'me' ? (
                    <span className="form-hint muted">{meRecipientWarning(senderMode)}</span>
                  ) : null}
                </label>
                <label>
                  Display name
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="optional"
                    aria-label="Display name"
                  />
                </label>
                {teamUsers.length > 0 ? (
                  <label>
                    Owner
                    <select
                      value={ownerUserId}
                      onChange={(e) => setOwnerUserId(e.target.value)}
                      aria-label="Recipient owner"
                    >
                      <option value="">Default (you)</option>
                      {teamUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name?.trim() || u.email || u.id}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {createResolutionMeta?.source !== 'known_chat' ? (
                <label>
                  Telegram delivery method
                  <select
                    value={createResolutionMeta?.delivery_provider ?? createDeliveryProvider}
                    onChange={(e) =>
                      setCreateDeliveryProvider(e.target.value as TelegramDeliveryProvider)
                    }
                    aria-label="Telegram delivery method"
                  >
                    {DELIVERY_PROVIDER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="form-hint muted">
                    {
                      DELIVERY_PROVIDER_OPTIONS.find(
                        (option) =>
                          option.value ===
                          (createResolutionMeta?.delivery_provider ?? createDeliveryProvider),
                      )?.hint
                    }
                  </span>
                </label>
                ) : null}
                <label>
                  Visibility
                  <select
                    value={createVisibility}
                    onChange={(e) =>
                      setCreateVisibility(e.target.value as 'private' | 'workspace')
                    }
                    aria-label="Recipient visibility"
                  >
                    <option value="private">Private — only you</option>
                    <option value="workspace">Workspace — visible to all members</option>
                  </select>
                </label>
                <RecipientEnabledField
                  id="recipient-create-enabled"
                  checked={createEnabled}
                  onChange={setCreateEnabled}
                  className="admin-form-grid__full"
                />
              </div>
              <StickyFormBar>
                <LoadingButton type="submit" variant="primary" loading={creating} loadingLabel="Adding…">
                  Add recipient
                </LoadingButton>
              </StickyFormBar>
            </form>
          </CollapsiblePanel>
          </div>
        )}

        <CollapsiblePanel
          id="recipient-list"
          storageScope={PANEL_SCOPE}
          title="Recipients"
          badge={initialLoading ? '…' : recipients.length}
          expanded={listPanelExpanded}
          onExpandedChange={setListPanelExpanded}
          defaultExpanded
          unmountWhenCollapsed
          loading={initialLoading}
        >
          {recipients.length === 0 ? (
            <EmptyState
              title="No recipients configured yet"
              description="Add a Telegram chat ID or username, then subscribe them to a search."
              action={
                canCreate ? (
                  <span className="muted">Expand Add recipient above to get started.</span>
                ) : undefined
              }
            />
          ) : (
            <>
              {canEdit ? (
                <BulkActionBar
                  selectedCount={bulk.selectedCount}
                  totalFiltered={recipients.length}
                  onClear={bulk.clearSelection}
                  onSelectPage={() => bulk.togglePage(recipients.map((r) => r.id))}
                  onSelectFiltered={() => bulk.selectFiltered(recipients.map((r) => r.id))}
                  loading={bulkBusy}
                >
                  <BulkActionButton
                    loading={bulkBusy}
                    onClick={async () => {
                      const ids = Array.from(bulk.selectedKeys);
                      if (!ids.length) return;
                      setBulkBusy(true);
                      try {
                        for (const id of ids) {
                          const r = recipients.find((x) => x.id === id);
                          if (r) await api.recipients.update(id, { is_enabled: true });
                        }
                        toast(`Enabled ${ids.length} recipient(s)`, 'success');
                        bulk.clearSelection();
                        await reload();
                      } catch (err) {
                        toast(err instanceof Error ? err.message : 'Bulk enable failed', 'error');
                      } finally {
                        setBulkBusy(false);
                      }
                    }}
                  >
                    Enable
                  </BulkActionButton>
                  <BulkActionButton
                    loading={bulkBusy}
                    onClick={async () => {
                      const ids = Array.from(bulk.selectedKeys);
                      if (!ids.length) return;
                      setBulkBusy(true);
                      try {
                        for (const id of ids) {
                          await api.recipients.update(id, { is_enabled: false });
                        }
                        toast(`Disabled ${ids.length} recipient(s)`, 'info');
                        bulk.clearSelection();
                        await reload();
                      } catch (err) {
                        toast(err instanceof Error ? err.message : 'Bulk disable failed', 'error');
                      } finally {
                        setBulkBusy(false);
                      }
                    }}
                  >
                    Disable
                  </BulkActionButton>
                </BulkActionBar>
              ) : null}
              <DataTable
                columns={recipientColumns}
                rows={recipients}
                getRowKey={(r) => r.id}
                getRowClassName={(r) => (!r.is_enabled ? 'row-muted' : undefined)}
                columnStorageKey="recipients"
                selection={
                  canEdit
                    ? {
                        isSelected: bulk.isSelected,
                        onToggleRow: bulk.toggleRow,
                        pageKeys: recipients.map((r) => r.id),
                        pageAllSelected: bulk.pageAllSelected(recipients.map((r) => r.id)),
                        pageSomeSelected: bulk.pageSomeSelected(recipients.map((r) => r.id)),
                        onTogglePage: () => bulk.togglePage(recipients.map((r) => r.id)),
                      }
                    : undefined
                }
                toolbar={{
                  searchPlaceholder: 'Filter recipients…',
                  onRefresh: () => void reload(),
                  onExportCsv: () => exportTableCsv(recipientColumns, recipients, 'recipients.csv'),
                }}
              />
            </>
          )}
        </CollapsiblePanel>
      </AdminPageSections>

      {editRecipient && (
        <EditRecipientModal
          recipient={editRecipient}
          botConfigured={botConfigured}
          sessionExists={tgStatus?.session_exists ?? false}
          senderMode={senderMode}
          onClose={() => setEditRecipient(null)}
          onSaved={(msg) => {
            toast(msg, 'success');
            void reload();
          }}
          onError={(msg) => toast(msg, 'error')}
        />
      )}

      {deleteRecipient && (
        <ConfirmRecipientDeleteModal
          recipient={deleteRecipient}
          onClose={() => setDeleteRecipient(null)}
          onDone={(msg) => {
            toast(msg, 'success');
            void reload();
          }}
        />
      )}

      {(sendRecipient || showSendForm) && (
        <SendMessageModal
          recipient={sendRecipient ?? undefined}
          recipients={recipients}
          onClose={() => {
            setSendRecipient(null);
            setShowSendForm(false);
          }}
          onDone={(msg) => toast(msg, msg.includes('failed') ? 'error' : 'success')}
        />
      )}

      {knownChats && (
        <KnownChatsModal
          chats={knownChats}
          creatingChatId={creatingKnownChatId}
          loading={checkingUpdates}
          refreshing={refreshingKnownChats}
          notice={knownChatsNotice}
          refreshError={knownChatsRefreshError}
          lastRefreshedAt={knownChatsLastRefreshed}
          onRefresh={() => refreshKnownChats()}
          onClose={() => {
            setKnownChats(null);
            setKnownChatsNotice(null);
            setKnownChatsRefreshError(null);
          }}
          onUse={handleKnownChatUse}
        />
      )}
      {canCreate && isCompactNav ? (
        <FloatingActionButton
          label="Add recipient"
          onClick={() => {
            setAddPanelExpanded(true);
            document.getElementById('recipient-create-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      ) : null}
    </div>
  );
}
